import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import type { GalleryImage } from "../campaignTypes";
import {
  PORTAL_MAX_WIDTH,
  bodySx,
  eyebrowSx,
  portalFont,
  sectionTitleSx,
} from "../partnerTheme";
import PendingMediaPlaceholder from "./PendingMediaPlaceholder";

function GalleryCardShell({
  img,
  children,
  onOpenLightbox,
}: {
  img: GalleryImage;
  children: ReactNode;
  onOpenLightbox: () => void;
}) {
  const sharedSx = {
    gridColumn: {
      xs: "auto",
      md: img.size === "wide" ? "span 2" : "span 1",
    },
    p: 0,
    border: "1px solid var(--portal-border)",
    borderRadius: "20px",
    overflow: "hidden",
    cursor: "pointer",
    bgcolor: "var(--portal-surface)",
    textAlign: "left" as const,
    textDecoration: "none",
    color: "inherit",
    display: "block",
    transition: "transform 0.2s ease",
    "&:hover": { transform: "translateY(-2px)" },
    "&:focus-visible": {
      outline: "2px solid var(--portal-accent)",
      outlineOffset: 2,
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": { transform: "none" },
    },
  };

  const label = img.badge ? `${img.badge}: ${img.caption}` : img.caption;

  if (img.href) {
    return (
      <Box
        component="a"
        href={img.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${label}`}
        sx={sharedSx}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={onOpenLightbox}
      aria-label={`Open photo: ${label}`}
      sx={sharedSx}
    >
      {children}
    </Box>
  );
}

function GalleryCard({
  img,
  onOpenLightbox,
}: {
  img: GalleryImage;
  onOpenLightbox: () => void;
}) {
  return (
    <GalleryCardShell img={img} onOpenLightbox={onOpenLightbox}>
      <Box sx={{ position: "relative" }}>
        <Box
          component="img"
          src={img.src}
          alt={img.alt}
          loading="lazy"
          decoding="async"
          sx={{
            width: "100%",
            height: { xs: 220, md: img.size === "wide" ? 280 : 240 },
            objectFit: "cover",
            display: "block",
          }}
        />
        {img.badge ? (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              px: 1.25,
              py: 0.5,
              borderRadius: "999px",
              bgcolor: "rgba(8,10,12,0.82)",
              border: "1px solid rgba(255,255,255,0.18)",
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#fff",
              }}
            >
              {img.badge}
            </Typography>
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.75,
          py: 1.25,
        }}
      >
        <Typography
          sx={{
            fontFamily: portalFont,
            fontSize: "0.875rem",
            color: "var(--portal-text)",
            fontWeight: 600,
          }}
        >
          {img.caption}
        </Typography>
        {img.href ? (
          <OpenInNewIcon
            sx={{ fontSize: 16, color: "var(--portal-text-muted)", flexShrink: 0 }}
          />
        ) : null}
      </Box>
    </GalleryCardShell>
  );
}

function GalleryGroup({
  heading,
  blurb,
  images,
  onOpenLightbox,
}: {
  heading: string;
  blurb: string;
  images: GalleryImage[];
  onOpenLightbox: (img: GalleryImage) => void;
}) {
  if (!images.length) return null;
  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        sx={{
          fontFamily: portalFont,
          fontWeight: 700,
          color: "var(--portal-text)",
          fontSize: { xs: "1.15rem", md: "1.35rem" },
          letterSpacing: "-0.02em",
          mb: 0.75,
        }}
      >
        {heading}
      </Typography>
      <Typography sx={{ ...bodySx, maxWidth: 640, mb: 2, fontSize: "0.925rem" }}>
        {blurb}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(4, 1fr)",
          },
          gap: 1.5,
        }}
      >
        {images.map((img) => (
          <GalleryCard key={img.id} img={img} onOpenLightbox={() => onOpenLightbox(img)} />
        ))}
      </Box>
    </Box>
  );
}

export default function PhotoGallery({
  eyebrow,
  title,
  description,
  images,
  pendingMessage,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  images: GalleryImage[];
  pendingMessage?: string;
}) {
  const lightboxImages = images.filter((img) => !img.href);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const photoGalleries = useMemo(
    () => images.filter((img) => (img.badge || "").toLowerCase().includes("photo")),
    [images]
  );
  const tapingFiles = useMemo(
    () => images.filter((img) => (img.badge || "").toLowerCase().includes("taping")),
    [images]
  );
  const otherImages = useMemo(
    () =>
      images.filter(
        (img) =>
          !photoGalleries.includes(img) && !tapingFiles.includes(img)
      ),
    [images, photoGalleries, tapingFiles]
  );
  const useGroupedLayout = photoGalleries.length > 0 && tapingFiles.length > 0;

  const close = useCallback(() => setLightboxIndex(null), []);
  const go = useCallback(
    (delta: number) => {
      setLightboxIndex((current) => {
        if (current == null || lightboxImages.length === 0) return current;
        return (current + delta + lightboxImages.length) % lightboxImages.length;
      });
    },
    [lightboxImages.length]
  );

  useEffect(() => {
    if (lightboxIndex == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, close, go]);

  useEffect(() => {
    if (lightboxIndex != null) closeRef.current?.focus();
  }, [lightboxIndex]);

  if (!images.length && !pendingMessage) return null;

  const active = lightboxIndex != null ? lightboxImages[lightboxIndex] : null;

  function openLightboxFor(img: GalleryImage) {
    const idx = lightboxImages.findIndex((item) => item.id === img.id);
    if (idx >= 0) setLightboxIndex(idx);
  }

  return (
    <Box
      id="gallery"
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2, md: 4 },
        py: { xs: 6, md: 10 },
        scrollMarginTop: 96,
      }}
    >
      <Typography sx={eyebrowSx}>{eyebrow}</Typography>
      <Typography sx={sectionTitleSx}>{title}</Typography>
      {description ? (
        <Typography sx={{ ...bodySx, maxWidth: 720, mb: 4 }}>{description}</Typography>
      ) : (
        <Box sx={{ mb: 4 }} />
      )}

      {!images.length && pendingMessage ? (
        <PendingMediaPlaceholder message={pendingMessage} />
      ) : useGroupedLayout ? (
        <>
          <GalleryGroup
            heading="Photo galleries"
            blurb="Event photography from each room. Opens the Google Drive folder for that taping."
            images={photoGalleries}
            onOpenLightbox={openLightboxFor}
          />
          <GalleryGroup
            heading="Taping files"
            blurb="Full set folders from each taping. Opens the Dropbox folder for that night."
            images={tapingFiles}
            onOpenLightbox={openLightboxFor}
          />
          {otherImages.length ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
                gap: 1.5,
              }}
            >
              {otherImages.map((img) => (
                <GalleryCard key={img.id} img={img} onOpenLightbox={() => openLightboxFor(img)} />
              ))}
            </Box>
          ) : null}
        </>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: images.length >= 6 ? "repeat(4, 1fr)" : images.length === 4 ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
            },
            gap: 1.5,
          }}
        >
          {images.map((img) => (
            <GalleryCard key={img.id} img={img} onOpenLightbox={() => openLightboxFor(img)} />
          ))}
        </Box>
      )}

      <Dialog
        open={active != null}
        onClose={close}
        maxWidth={false}
        aria-labelledby="portal-gallery-title"
        PaperProps={{
          sx: {
            bgcolor: "rgba(8,8,10,0.96)",
            m: { xs: 1, sm: 2 },
            maxWidth: "min(1100px, 96vw)",
            width: "100%",
            borderRadius: "20px",
            border: "1px solid var(--portal-border)",
            overflow: "hidden",
          },
        }}
      >
        {active ? (
          <Box sx={{ position: "relative" }}>
            <IconButton
              ref={closeRef}
              aria-label="Close gallery"
              onClick={close}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 2,
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.5)",
              }}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              aria-label="Previous photo"
              onClick={() => go(-1)}
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.5)",
                display: { xs: "none", sm: "inline-flex" },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              aria-label="Next photo"
              onClick={() => go(1)}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.5)",
                display: { xs: "none", sm: "inline-flex" },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
            <Box
              component="img"
              src={active.src}
              alt={active.alt}
              sx={{
                width: "100%",
                maxHeight: { xs: "70vh", md: "78vh" },
                objectFit: "contain",
                display: "block",
                bgcolor: "#000",
              }}
            />
            <Box sx={{ p: 2.25 }}>
              <Typography
                id="portal-gallery-title"
                sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 600, mb: 0.5 }}
              >
                {active.caption}
              </Typography>
              <Typography sx={{ fontFamily: portalFont, color: "var(--portal-text-muted)", fontSize: "0.875rem" }}>
                {lightboxIndex != null ? `${lightboxIndex + 1} of ${lightboxImages.length}` : ""}
              </Typography>
              <Box sx={{ display: { xs: "flex", sm: "none" }, gap: 1, mt: 2 }}>
                <Button fullWidth variant="outlined" onClick={() => go(-1)} sx={{ color: "#fff", borderColor: "var(--portal-border)", textTransform: "none" }}>
                  Previous
                </Button>
                <Button fullWidth variant="outlined" onClick={() => go(1)} sx={{ color: "#fff", borderColor: "var(--portal-border)", textTransform: "none" }}>
                  Next
                </Button>
              </Box>
            </Box>
          </Box>
        ) : null}
      </Dialog>
    </Box>
  );
}
